import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Box,
    useMediaQuery,
    useTheme,
    Button,
    IconButton,
    Tooltip,
    CircularProgress,
    Table,
    TableBody,
    TableRow,
    TableCell,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { fetchFundsViewV2, payoutTransactions } from "../../services/backendAPI";
import { DEPOSIT_URL } from "../../utils/constant";
import { useSelector } from "react-redux";
import { fetchFundDetails, fetchPayoutTransactions, fetchFundsView } from "../../store/actions/funds";
import { useDispatch } from "react-redux";
import TransactionView from './TransactionView';
import { getValueColor } from '../../utils/utilfunction';
import useTouchInteraction from '../../hooks/useTouchInteraction';
import { ContactlessOutlined } from "@mui/icons-material";
import WithdrawModal from "../Profile/WithdrawModal";
import WithdrawButton from './WithdrawButton';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import useBalance from "../Portfolio/Positions/useBalance";
import { updateCalculatedFunds } from "../../store/actionCreators/fundsActions";
import AddFundsModal from '../Common/AddFundsModal';
import { brokerConfig } from '../../config/brokerConfig';
import PaymentGuidelinesModal from '../Common/PaymentGuidelinesModal';
import PageTabs from '../Common/PageTabs';

const FUND_TABS = [
    { label: "Info", value: 0 },
    { label: "Transactions", value: 1 },
];

const Funds = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const location = useLocation();
    const payInComponentRef = useRef();

    // Use the global balance hook instead of local calculations
    const balanceData = useBalance();

    // Get position metrics from Redux store
    const positionMetrics = useSelector(state => state.portfolio.positionMetrics || {});

    // Format currency for display
    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '₹0.00';

        const value = parseFloat(amount);
        const absValue = Math.abs(value);
        const sign = value < 0 ? '-' : '';

        if (absValue >= 10000000) {
            return `${sign}₹${(absValue / 10000000).toFixed(2)} Cr`;
        } else if (absValue >= 100000) {
            return `${sign}₹${(absValue / 100000).toFixed(2)} L`;
        } else if (absValue >= 1000) {
            return `${sign}₹${(absValue / 1000).toFixed(2)} K`;
        }

        return `${sign}₹${absValue.toFixed(2)}`;
    };

    async function callHandleAddFundsFromPayIn() {
        await payInComponentRef.current.handleAddFunds();
    }

    // Get color based on profit/loss
    const getPnlColor = (value) => {
        if (!value || value === 0) return theme.palette.text.secondary;
        return value > 0 ? theme.palette.success.main : theme.palette.error.main;
    };

    const { userData, userSettings } = useSelector(state => state.profile);
    const [selectedTab, setSelectedTab] = useState(0);
    const [selectedFundType, setFundType] = useState("ALL");
    const [funds, setFundDetails] = useState([]);
    const fundDetails = useSelector((state) => state.funds.fundDetails);
    const payoutTransactions = useSelector((state) => state.funds.payoutTransactions.list);
    const availableFunds = useSelector((state) => state.funds.fundsViewV2);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [pageNo, setPageNo] = useState(1);

    useEffect(() => {
        dispatch(fetchFundsView());
    }, []);

    // Add touch interaction for Add Funds button
    const handleAddFundsClick = useCallback(() => {
        if (!userData?.phone_number) {
            console.warn('User data or phone number not available');
            return;
        }
        navigate("/payin", { replace: true })
    }, [userData]);

    const { handlers: addFundsHandlers, touchStates: addFundsTouchStates } = useTouchInteraction(handleAddFundsClick, true);

    useEffect(() => {
        const client_id = localStorage.getItem("login_id");
        // Refresh the calculated funds data
        dispatch(updateCalculatedFunds());
        dispatch(fetchPayoutTransactions({ user_id: client_id, client_id: client_id, page_no: pageNo, page_size: 100, first_page: pageNo === 1, last_page: false, user_type: "CLIENT" }));
    }, [dispatch, pageNo]);

    useEffect(() => {
        setFundDetails(fundDetails);
    }, [fundDetails]);

    const handleTabChange = useCallback((event, newValue) => {
        setSelectedTab(newValue);
    }, []);

    const [refreshing, setRefreshing] = useState(false);

    // Refresh the data for whichever tab is active (with a spinning indicator).
    const handleRefresh = useCallback(async () => {
        const client_id = localStorage.getItem("login_id");
        setRefreshing(true);
        const minVisible = new Promise((resolve) => setTimeout(resolve, 600));
        try {
            if (selectedTab === 0) {
                await Promise.all([dispatch(updateCalculatedFunds()), dispatch(fetchFundsView()), minVisible]);
            } else {
                await Promise.all([
                    dispatch(fetchPayoutTransactions({ user_id: client_id, client_id, page_no: pageNo, page_size: 100, first_page: pageNo === 1, last_page: false, user_type: "CLIENT" })),
                    minVisible,
                ]);
            }
        } finally {
            setRefreshing(false);
        }
    }, [selectedTab, pageNo, dispatch]);

    const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
    const [isAddFundsModalOpen, setIsAddFundsModalOpen] = useState(false);
    const [isPaymentGuidelinesModalOpen, setIsPaymentGuidelinesModalOpen] = useState(false);

    // Feedback Modal State
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [hasCheckedFeedback, setHasCheckedFeedback] = useState(false);

    // Check if we should open PayIn modal based on navigation state
    useEffect(() => {
        if (location.state?.openPayInModal) {
            setIsAddFundsModalOpen(true);
            // Clear the state to prevent reopening on refresh
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, navigate, location.pathname]);

    // Handle Withdraw Click to intercept with Feedback
    const handleWithdrawClick = useCallback(() => {
        setIsWithdrawModalOpen(true);
    }, []);



    // Create a formatted object from balanceData to display in UI
    const createDisplayFundsObject = useCallback(() => {
        const allFunds = {
            "Balance": balanceData.formatted.tradingBalance,
            "Available Margin": balanceData.formatted.availableCash,
            "Unrealized P&L": balanceData.formatted.unrealizedPnl,
            // "Total P&L": balanceData.formatted.totalPnl,
            // "Realized P&L": balanceData.formatted.realizedPnl,
            "Blocked Margin": balanceData.formatted.blockedMargin,
            "Equity": balanceData.formatted.equity,
            "Equity Percentage": balanceData.formatted.equityPercentage,
            "Credit": balanceData.formatted.credit
        };
        // Always show Unrealized P&L and Blocked Margin, filter Credit if zero
        return Object.fromEntries(
            Object.entries(allFunds).filter(([key, value]) => {
                if (key === 'Credit') {
                    return parseFloat(value?.toString().replace(/[^\d.-]/g, '')) !== 0;
                }
                // Show all other fields, even if zero
                return key !== undefined;
            })
        );
    }, [balanceData]);

    // Get the funds data to display
    const displayFunds = createDisplayFundsObject();

    // Color for a metric value: P&L follows sign from the balance hook, others
    // turn red only when negative, otherwise neutral.
    const getMetricColor = (name, value) => {
        if (name.includes('P&L')) {
            const key = name.toLowerCase().includes('unrealized')
                ? 'unrealizedPnl'
                : name.toLowerCase().includes('realized')
                ? 'realizedPnl'
                : 'totalPnl';
            return balanceData.isPositive[key] ? theme.palette.success.main : theme.palette.error.main;
        }
        const numeric = parseFloat(String(value).replace(/[^\d.-]/g, ''));
        if (!isNaN(numeric) && numeric < 0) return theme.palette.error.main;
        return theme.palette.text.primary;
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                backgroundColor: 'background.default',
                [theme.breakpoints.down('sm')]: {
                    // 56 AppBar + 30 Top Ticker = 86 top; + 56 BottomNav = 142
                    height: 'calc(100vh - 142px)',
                    position: 'fixed',
                    top: 86,
                    left: 0,
                    right: 0,
                    bottom: 56,
                },
            }}
        >
            <PageTabs
                value={selectedTab}
                onChange={handleTabChange}
                tabs={FUND_TABS}
                sticky
                action={
                    isMobile ? (
                        <Tooltip title={refreshing ? 'Refreshing…' : 'Refresh'}>
                            <IconButton onClick={handleRefresh} size="small" color="primary">
                                {refreshing ? (
                                    <CircularProgress size={18} thickness={5} color="primary" />
                                ) : (
                                    <RefreshIcon fontSize="small" />
                                )}
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Button
                                variant="outlined"
                                color="success"
                                size="small"
                                startIcon={<AccountBalanceWalletIcon />}
                                onClick={handleWithdrawClick}
                                sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
                            >
                                Withdraw
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => setIsAddFundsModalOpen(true)}
                                sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
                            >
                                Add Funds
                            </Button>
                            <Button
                                startIcon={
                                    refreshing ? (
                                        <CircularProgress size={16} thickness={5} color="inherit" />
                                    ) : (
                                        <RefreshIcon />
                                    )
                                }
                                onClick={handleRefresh}
                                size="small"
                            >
                                {refreshing ? 'Refreshing…' : 'Refresh'}
                            </Button>
                        </Box>
                    )
                }
            />

            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {selectedTab === 0 && (
                    <Box sx={{
                        flex: 1,
                        overflow: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        overscrollBehavior: 'none',
                        p: { xs: 1, sm: 2 },
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                    }}>
                        <Table size="small">
                            <TableBody>
                                {Object.entries(displayFunds).map(([name, value]) => (
                                    <TableRow key={name} hover>
                                        <TableCell
                                            sx={{
                                                color: 'text.secondary',
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                borderColor: 'divider',
                                                py: 1.5,
                                            }}
                                        >
                                            {name}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                fontWeight: 600,
                                                color: getMetricColor(name, value),
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                borderColor: 'divider',
                                                py: 1.5,
                                            }}
                                        >
                                            {value}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}

                {selectedTab === 1 && (
                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <TransactionView payoutTransactions={payoutTransactions} pageNo={pageNo} setPageNo={setPageNo} />
                    </Box>
                )}
            </Box>

            {/* Bottom action bar — mobile only (desktop has these in the tab bar) */}
            <Box
                sx={{
                    display: { xs: 'flex', sm: 'none' },
                    gap: 1.5,
                    p: 1.5,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    justifyContent: 'stretch',
                }}
            >
                <Button
                    variant="outlined"
                    color="success"
                    startIcon={<AccountBalanceWalletIcon />}
                    onClick={handleWithdrawClick}
                    sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 600, flex: { xs: 1, sm: 'none' }, minWidth: { sm: 140 } }}
                >
                    Withdraw
                </Button>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setIsAddFundsModalOpen(true)}
                    sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 600, flex: { xs: 1, sm: 'none' }, minWidth: { sm: 140 } }}
                >
                    Add Funds
                </Button>
            </Box>
            <WithdrawModal open={isWithdrawModalOpen} onClose={() => setIsWithdrawModalOpen(false)} />
            <AddFundsModal
                open={isAddFundsModalOpen}
                onClose={() => setIsAddFundsModalOpen(false)}
                ref={payInComponentRef}
                setIsPaymentGuidelinesModalOpen={setIsPaymentGuidelinesModalOpen}
            />
            <PaymentGuidelinesModal
                open={isPaymentGuidelinesModalOpen}
                onClose={() => setIsPaymentGuidelinesModalOpen(false)}
                onProceed={() => callHandleAddFundsFromPayIn()}
            />
        </Box>
    );
};

export default Funds;